// SET operations

/**
 * Checks if the first Set/Array is a superset of the second Set/Array.
 *
 * @param {Set|Array} set - The first Set or Array to compare.
 * @param {Set|Array} subset - The second Set or Array to compare.
 * @returns {boolean} True if the first set is a superset of the second set, false otherwise.
 */
function isSuperset(set, subset) {
    const _set = set instanceof Set ? set : new Set(set);
    const _subset = subset instanceof Set ? subset : new Set(subset);
    for (const elem of _subset) {
        if (!_set.has(elem)) {
            return false;
        }
    }
    return true;
}

function union(setA, setB) {
    const _union = new Set(setA);
    for (const elem of setB) {
        _union.add(elem);
    }
    return _union;
}

/**
 * Computes the union of two arrays and returns the result as an Array.
 *
 * @param {Array} collectionA - The first array to compare.
 * @param {Array} collectionB - The second array to compare.
 * @returns {Array} An Array containing the elements that are present in either of the arrays.
 */
function arrayUnion(collectionA, collectionB) {
    const _union = new Set(collectionA);
    for (const elem of collectionB) {
        _union.add(elem);
    }
    return Array.from(_union);
}


function intersection(setA, setB) {
    const _intersection = new Set();
    for (const elem of setB) {
        if (setA.has(elem)) {
            _intersection.add(elem);
        }
    }
    return _intersection;
}

/**
 * Computes the intersection of two arrays and returns the result as an Array.
 *
 * @param {Array} collectionA - The first array to compare.
 * @param {Array} collectionB - The second array to compare.
 * @returns {Array} An Array containing the elements that are present in both arrays.
 */
function arrayIntersection(collectionA, collectionB) {
    const setA = new Set(collectionA);
    const setB = new Set(collectionB);
    const _intersection = new Set();
    for (const elem of setB) {
        if (setA.has(elem)) {
            _intersection.add(elem);
        }
    }
    return Array.from(_intersection);
}

function symmetricDifference(setA, setB) {
    const _difference = new Set(setA);
    for (const elem of setB) {
        if (_difference.has(elem)) {
            _difference.delete(elem);
        } else {
            _difference.add(elem);
        }
    }
    return _difference;
}

function difference(setA, setB) {
    const _difference = new Set(setA);
    for (const elem of setB) {
        _difference.delete(elem);
    }
    return _difference;
}

const makeUnique = (arr) => {
    // pass in an ARRAY, return an ARRAY of unique values 
    return [...new Set(arr)];
}

// Examples
// const setA = new Set([1, 2, 3, 4]);
// const setB = new Set([2, 3]);
// const setC = new Set([3, 4, 5, 6]);

// isSuperset(setA, setB); // returns true
// union(setA, setC); // returns Set {1, 2, 3, 4, 5, 6}
// intersection(setA, setC); // returns Set {3, 4}
// symmetricDifference(setA, setC); // returns Set {1, 2, 5, 6}
// difference(setA, setC); // returns Set {1, 2}

// Use the regular Set constructor to transform an Array into a Set
// const mySet = new Set(myArray);

// Use the spread syntax to transform a set into an Array.
// console.log([...mySet]); // Will show you exactly the same Array as myArray


export {
    isSuperset,
    union,
    intersection,
    symmetricDifference,
    difference,
    makeUnique,
    arrayUnion,
    arrayIntersection,
}